import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { catchError, finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzTooltipModule } from "ng-zorro-antd/tooltip";

import { GoogleFileDto, PermissionEnum, ProductDto } from "../../../dto";
import { HttpProductService } from "../../../services/product/http-product.service";
import { DriveFilePickerModalComponent } from "../../../components/drive-file-picker/drive-file-picker-modal.component";
import { DriveFileCardComponent } from "../../../components/drive-file-card/drive-file-card.component";
import { AuthService } from "../../../services/auth/auth.service";
import { UnauthorizedAccessEditWarningComponent } from "../../../components/unauthorized-access-edit-warning/unauthorized-access-edit-warning.component";

@Component({
    selector: 'app-product-editor-modal',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule,
        NzModalModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule,
        NzIconModule,
        NzTooltipModule,
        DriveFilePickerModalComponent,
        UnauthorizedAccessEditWarningComponent,
        DriveFileCardComponent
    ],
    templateUrl: './product-editor-modal.component.html',
    styleUrls: ['./product-editor-modal.component.scss']
})
export class ProductEditorModalComponent implements OnInit {
    @Input() modalId = 'productEditorModal';
    @Output() saved = new EventEmitter<ProductDto>();
    @Output() cancelled = new EventEmitter<void>();

    editingProductId?: number; 
    modalTitle = 'Add Product';

    form!: FormGroup;
    
    isVisible = false;
    isConfirmLoading = false;

    // Tracks which file type the picker is currently fetching
    currentPickerTarget: 'logo' | 'brochure' | 'datasheet' | null = null;

    @ViewChild(DriveFilePickerModalComponent) drivePicker!: DriveFilePickerModalComponent;

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private httpService: HttpProductService,
        private authService: AuthService
    ) { }

    get canEdit(): boolean {
        return this.authService.hasAccess(PermissionEnum.ProductsEdit);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
            description: ['', [Validators.minLength(2), Validators.maxLength(2000)]],
            link: ['', [Validators.minLength(2), Validators.maxLength(100)]],

            // Logo Fields
            logoDriveFileId: [null],
            logoDriveFolderId: [null],
            logoDriveFileName: [null],
            logoDriveFileLink: [null],

            // Brochure Fields
            brochureDriveFileId: [null],
            brochureDriveFolderId: [null],
            brochureDriveFileName: [null],
            brochureDriveFileLink: [null],

            // Datasheet Fields
            datasheetDriveFileId: [null],
            datasheetDriveFolderId: [null],
            datasheetDriveFileName: [null],
            datasheetDriveFileLink: [null],
        });
    }

    open(product?: ProductDto) {
        this.form.reset();
        
        if (product) {
            this.editingProductId = product.id;
            this.modalTitle = 'Edit Product';
            this.form.patchValue({
                name: product.name,
                description: product.description,
                link: product.link,
                
                logoDriveFileId: product.logoDriveFile?.driveFileId,
                logoDriveFolderId: product.logoDriveFile?.driveFolderId,
                logoDriveFileName: product.logoDriveFile?.driveFileName,
                logoDriveFileLink: product.logoDriveFile?.driveFileLink,

                brochureDriveFileId: product.brochureDriveFile?.driveFileId,
                brochureDriveFolderId: product.brochureDriveFile?.driveFolderId,
                brochureDriveFileName: product.brochureDriveFile?.driveFileName,
                brochureDriveFileLink: product.brochureDriveFile?.driveFileLink,

                datasheetDriveFileId: product.datasheetDriveFile?.driveFileId,
                datasheetDriveFolderId: product.datasheetDriveFile?.driveFolderId,
                datasheetDriveFileName: product.datasheetDriveFile?.driveFileName,
                datasheetDriveFileLink: product.datasheetDriveFile?.driveFileLink,
            });
        } 
        else {
            this.editingProductId = undefined;
            this.modalTitle = 'Add Product';
        }

        this.isVisible = true;

        if (!this.canEdit) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    handleCancel(): void {
        this.isVisible = false;
        this.cancelled.emit();
    }

    openDrivePicker(target: 'logo' | 'brochure' | 'datasheet') {
        this.currentPickerTarget = target;
        const currentFolderId = this.form.get(`${target}DriveFolderId`)?.value; 

        if (currentFolderId) {
            this.drivePicker.open(currentFolderId, target);
        } else {
            this.drivePicker.open(undefined, target);
        }
    }

    onDriveFileSelected(input: { file: GoogleFileDto, folderId?: string }) {
        if (!this.currentPickerTarget) return;

        const file = input.file;
        let link = file.webViewLink;
        if (!link) {
            link = `https://drive.google.com/file/d/${file.id}/view`;
        }

        this.form.patchValue({
            [`${this.currentPickerTarget}DriveFileId`]: file.id,
            [`${this.currentPickerTarget}DriveFolderId`]: input.folderId,
            [`${this.currentPickerTarget}DriveFileName`]: file.name,
            [`${this.currentPickerTarget}DriveFileLink`]: link
        });
        
        this.form.get(`${this.currentPickerTarget}DriveFileId`)?.markAsDirty();
        this.currentPickerTarget = null; // reset
    }

    clearFile(target: 'logo' | 'brochure' | 'datasheet') {
        this.form.patchValue({
            [`${target}DriveFileId`]: null,
            [`${target}DriveFolderId`]: null,
            [`${target}DriveFileName`]: null,
            [`${target}DriveFileLink`]: null
        });
    }

    save(): void {
        if (this.form.invalid) {
            Object.values(this.form.controls).forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.updateValueAndValidity({ onlySelf: true });
                }
            });
            return;
        }

        this.isConfirmLoading = true; 

        const payload: ProductDto = {
            id: this.editingProductId,
            name: this.form.value.name,
            description: this.form.value.description,
            link: this.form.value.link,
            
            logoDriveFile: this.form.value.logoDriveFileId ? {
                driveFileId: this.form.value.logoDriveFileId,
                driveFolderId: this.form.value.logoDriveFolderId,
                driveFileName: this.form.value.logoDriveFileName,
                driveFileLink: this.form.value.logoDriveFileLink
            } : undefined,

            brochureDriveFile: this.form.value.brochureDriveFileId ? {
                driveFileId: this.form.value.brochureDriveFileId,
                driveFolderId: this.form.value.brochureDriveFolderId,
                driveFileName: this.form.value.brochureDriveFileName,
                driveFileLink: this.form.value.brochureDriveFileLink
            } : undefined,

            datasheetDriveFile: this.form.value.datasheetDriveFileId ? {
                driveFileId: this.form.value.datasheetDriveFileId,
                driveFolderId: this.form.value.datasheetDriveFolderId,
                driveFileName: this.form.value.datasheetDriveFileName,
                driveFileLink: this.form.value.datasheetDriveFileLink
            } : undefined
        };

        // Notice this is now sending standard JSON, not FormData
        this.httpService.saveProduct(payload)
            .pipe(
                catchError(error => {
                    console.error('Error adding product', error);
                    this.toastr.error('Failed to save product: ' + (error.error?.error || error.message));
                    throw error;
                }),
                finalize(() => this.isConfirmLoading = false) 
            )
            .subscribe(data => {
                if (!data.success) { // adjust depending on your API wrapper
                    this.toastr.error('Failed to save product');
                    return;
                }
                this.toastr.success('Product saved successfully');
                this.saved.emit(payload);
                this.isVisible = false;
            });
    }

    copyToClipboard(text: string | null | undefined, fieldName: string) {
        if (!text) return;
        navigator.clipboard.writeText(text).then(
            () => this.toastr.success(`${fieldName} copied to clipboard`),
            () => this.toastr.error(`Failed to copy ${fieldName}`)
        );
    }
}